import { ContinueToNextViewButton } from "@faire/retailer-visitor-shared/components/AppSignIn/components/ContinueToNextViewButton";
import { convertErrorToLoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/convertErrorToLoginError";
import { RadioMFAOption } from "@faire/retailer-visitor-shared/components/AppSignIn/components/RadioMFAOption";
import { LoginWrapper } from "@faire/retailer-visitor-shared/components/AppSignIn/components/styled";
import { View } from "@faire/retailer-visitor-shared/components/AppSignIn/components/View";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import login from "@faire/web-api--source/endpoints/www/api/v2/users/login/post";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { ILoginResponse } from "@faire/web-api--source/indigofair/data/ILoginResponse";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { LoginState } from "@faire/web-api--source/indigofair/data/LoginState";
import { MfaMethod } from "@faire/web-api--source/indigofair/data/MfaMethod";
import React, { FC, useState, useMemo } from "react";
import styled from "styled-components";

type SelectMFAOptionResult = {
  nextView: View;
  email?: string;
  mfaDescription?: string;
  mfaTitle?: string;
  mfaMethod?: keyof typeof MfaMethod;
  userToken?: string;
  isMFAFromEmail?: boolean;
};

interface IProps {
  email: string;
  mfaOptions: ILoginResponse.IMfaOption[];
  userToken?: string;
  password?: string;
  onComplete: (result: SelectMFAOptionResult) => void;
  onLogin: (user: IUser) => void;
  isMFAFromEmail?: boolean;
}

export const SelectMFAOptionView: FC<IProps> = ({
  email,
  mfaOptions,
  userToken,
  password,
  onComplete,
  onLogin,
  isMFAFromEmail,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { isMobile } = useViewport();

  const mfaOptionsMemoized: ILoginResponse.IMfaOption[] = useMemo(() => {
    if (isMobile) {
      // Move SMS MFA Method to front
      const mfaMethod = MfaMethod.SMS;
      if (!mfaOptions.some((option) => option.method === mfaMethod)) {
        return mfaOptions;
      }

      const mfaMethodIndex = mfaOptions.findIndex(
        (option) => option.method === mfaMethod
      );

      const updatedMfaOptions = mfaOptions.slice();
      const [mfaOptionToMove] = updatedMfaOptions.splice(mfaMethodIndex, 1);
      if (mfaOptionToMove) {
        updatedMfaOptions.unshift(mfaOptionToMove);
      }

      return updatedMfaOptions;
    }
    return mfaOptions;
  }, [isMobile, mfaOptions]);

  const [mfaOption, setMfaOption] = useState(mfaOptionsMemoized?.[0]?.method);
  const { isActive, ssoToken } = useSSOStore();
  const onContinue = async () => {
    try {
      const request = isActive
        ? ILoginRequest.build({
            mfa_method: mfaOption,
            sso_token: ssoToken,
            password,
            enable_mfa_method_selection: true,
          })
        : ILoginRequest.build({
            password,
            email_address: email,
            user_token: !email ? userToken : undefined,
            mfa_method: mfaOption,
            enable_mfa_method_selection: true,
          });
      const { mfa_description, mfa_title, login_state, user } =
        await login(request);
      if (login_state === LoginState.MFA_REQUIRED) {
        onComplete({
          nextView: View.ENTER_MFA,
          mfaDescription: mfa_description,
          mfaTitle: mfa_title,
          mfaMethod: mfaOption,
          isMFAFromEmail: isMFAFromEmail,
        });
        return;
      }

      onComplete({
        nextView: View.LOADING,
      });
      if (user) {
        onLogin(user);
      }
    } catch (e) {
      throw convertErrorToLoginError(e, strictLocalize);
    }
  };

  return (
    <LoginWrapper>
      <Typography variant="displaySSerifRegular">
        <StrictLocalMsg
          defaultMessage="Verify it’s you"
          description={{
            text: "MFA selection title above messaging around account safety and MFA reasoning",
            img: "https://cdn.faire.com/fastly/a4c66c67a9cc5976a311ca198ef39774593ac01e6579237d08a859071d3bf505.png",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="To keep your account safe, we want to make sure it’s really you. How would you like to receive your verification code?"
          description={{
            text: "MFA messaging around account safety and MFA reasoning below title",
            img: "https://cdn.faire.com/fastly/a4c66c67a9cc5976a311ca198ef39774593ac01e6579237d08a859071d3bf505.png",
          }}
        />
      </Typography>
      <Spacer height="3x" />
      <RadioMFAWrapper>
        {mfaOptionsMemoized.map(({ method, name, value }) =>
          method !== MfaMethod.MFA_METHOD_UNKNOWN ? (
            <Row key={method}>
              <RadioMFAOption
                active={mfaOption === method}
                title={name}
                value={value}
                onClick={() => setMfaOption(method)}
              />
            </Row>
          ) : null
        )}
      </RadioMFAWrapper>
      <Spacer height="4x" />
      <Continue onClick={onContinue} disabled={!mfaOption} />
    </LoginWrapper>
  );
};

const Continue = styled(ContinueToNextViewButton)`
  width: 100%;
`;

const RadioMFAWrapper = styled(Column).attrs({ gap: "2x" })``;
